import React, { memo } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * A phone number, rendered as a dial link.
 *
 * INTERNATIONAL FORMAT, ALWAYS (Sim, 25 Aug). This used to render
 * `formatNational()`, which is the number as someone standing IN that country
 * would dial it — so a Dubai contact stored as `+971504564184` printed as
 * `050 456 4184`, with the country code silently gone and nothing on screen to
 * say which country a leading 0 belonged to. With leases across regions the
 * national form is ambiguous at best and wrong at worst, so every number now
 * reads `+971 50 456 4184`. Australian numbers moved with it: `0412 345 678` is
 * now `+61 412 345 678`, in Contacts, Tenants, Task users and Settings alike.
 *
 * The `href` is E.164 rather than the formatted string — a `tel:` of the
 * national form only dials from inside that country, which was the other half
 * of the same bug.
 */
const PhoneNumber = ({ phone_number }: { phone_number: string }) => {
	if (!!phone_number) {
		// `^04` -> `614` assumes an AU mobile: this component predates non-AU data
		// and there are still stored numbers with no country code at all. It only
		// fires on a bare local number (anything already carrying `+` is untouched),
		// so a `+971...` passes through as itself.
		const strippedNumber = `+${phone_number.replace(/^04/, "614").replace(/[^0-9]/g, "")}`;
		const parsedNumber = parsePhoneNumberFromString(strippedNumber);
		if (parsedNumber) {
			return (
				<a className="contact-phone-number" href={`tel:${parsedNumber.number}`}>
					{parsedNumber.formatInternational()}
				</a>
			);
		}
		// Unparseable — show and dial the digits we were given rather than nothing.
		return (
			<a className="contact-phone-number" href={`tel:${strippedNumber}`}>
				{strippedNumber}
			</a>
		);
	}
	return <span className="muted phone-number">Unknown</span>;
};

export default memo(PhoneNumber);
